const RPC_TIMEOUT_MS = 30000;
const pending = new Map();
const listeners = new Map(); // event → Set<fn>

let _idCounter = 0;
const nextId = () => `rpc-${Date.now()}-${++_idCounter}`;

const isNative = () =>
  typeof window !== 'undefined' && !!window.ReactNativeWebView;

function handleIncoming(raw) {
  let msg;
  try { msg = typeof raw === 'string' ? JSON.parse(raw) : raw; } catch { return; }
  if (msg?.type === 'rpc-response' && pending.has(msg.id)) {
    const { resolve, reject, timer } = pending.get(msg.id);
    clearTimeout(timer);
    pending.delete(msg.id);
    if (msg.error) reject(Object.assign(new Error(msg.error.message), { code: msg.error.code }));
    else resolve(msg.result);
  } else if (msg?.type === 'event' && msg.event) {
    const set = listeners.get(msg.event);
    if (set) set.forEach((fn) => { try { fn(msg.payload); } catch {} });
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('message', (e) => handleIncoming(e.data));
  document.addEventListener('message', (e) => handleIncoming(e.data));
}

export function call(method, params = {}) {
  if (!isNative()) return callFallback(method, params);
  return new Promise((resolve, reject) => {
    const id = nextId();
    const timer = setTimeout(() => {
      pending.delete(id);
      reject(new Error(`RPC timeout: ${method}`));
    }, RPC_TIMEOUT_MS);
    pending.set(id, { resolve, reject, timer });
    window.ReactNativeWebView.postMessage(JSON.stringify({ id, type: 'rpc', method, params }));
  });
}

export function on(event, fn) {
  if (!listeners.has(event)) listeners.set(event, new Set());
  listeners.get(event).add(fn);
  return () => listeners.get(event).delete(fn);
}

// Browser fallback: just enough to keep dev server / tests running.
async function callFallback(method) {
  if (method === 'network.status') return { online: typeof navigator !== 'undefined' ? navigator.onLine : true, type: 'unknown' };
  if (method === 'app.getBackendUrl') return process.env.REACT_APP_BACKEND || '';
  if (method === 'app.getEnvironment') return { platform: 'web', isNative: false, version: 'fallback' };
  if (method === 'network.subscribe') {
    window.addEventListener('online', () => fireFallback('network.changed', { online: true, type: 'unknown' }));
    window.addEventListener('offline', () => fireFallback('network.changed', { online: false, type: 'none' }));
    return { subscribed: true };
  }
  // Offline behavior in browser is an explicit non-goal.
  throw Object.assign(new Error(`Bridge fallback does not implement: ${method}`),
    { code: 'BRIDGE_FALLBACK_NOT_IMPLEMENTED' });
}
function fireFallback(event, payload) {
  const set = listeners.get(event); if (set) set.forEach((fn) => fn(payload));
}

const bridge = { call, on };
export default bridge;
